import React from 'react';
import { useModal } from '../../components/Modal/PresentedModal';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { graphql } from '../../generated';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../model/notification';
import { t } from '../../locales/translate';
import { Button } from '../../components/Button/Button';
import { NewsForm, NewsFormFields } from './news_form';
import { parseJsonString } from '../../util/data';

const UPDATE_NEWS = graphql(`
  mutation UpdateNewsItem(
    $id: ID!
    $shopId: ID
    $titleDe: String
    $titleEn: String
    $textDe: String
    $textEn: String
    $category: String
    $url: String
    $inverse: Boolean
    $order: Int
    $asset: Upload
    $meta: JSON
  ) {
    updateNews(
      input: {
        id: $id
        shopId: $shopId
        titleDe: $titleDe
        titleEn: $titleEn
        textDe: $textDe
        textEn: $textEn
        category: $category
        url: $url
        inverse: $inverse
        order: $order
        asset: $asset
        meta: $meta
      }
    ) {
      clientMutationId
    }
  }
`);

const NEWS_ITEM = graphql(`
  query NewsItemForUpdate($id: ID!) {
    newsItem(id: $id) {
      id
      shopId
      title
      titleDe
      titleEn
      textDe
      textEn
      category
      url
      inverse
      order
      meta
      asset {
        fileType
        id
        ... on Image {
          url(width: 200)
        }
        ... on Video {
          url
        }
      }
    }
  }
`);

interface Props {
  id: string;
  submit: () => void;
}

export function UpdateNewsItem({ id, submit }: Props): JSX.Element {
  const { open, Modal, close } = useModal();
  const { data } = useSuspenseQuery(NEWS_ITEM, { variables: { id } });
  const [update] = useMutation(UPDATE_NEWS);
  const newsItem = data.newsItem;
  const asset =
    newsItem.asset && (newsItem.asset.__typename === 'Image' || newsItem.asset.__typename === 'Video')
      ? new File([], newsItem.asset.fileType)
      : undefined;

  const { fields, valid, dirty, onSubmit, submissionStatus } = useForm<NewsFormFields>({
    model: {
      shopId: newsItem.shopId!,
      titleDe: newsItem.titleDe,
      titleEn: newsItem.titleEn,
      textDe: newsItem.textDe,
      textEn: newsItem.textEn,
      category: newsItem.category,
      url: newsItem.url,
      inverse: newsItem.inverse,
      order: newsItem.order,
      meta: JSON.stringify(newsItem.meta, null, 2),
      asset: asset,
    },
    validations: {
      shopId: 'required',
      meta: 'json',
    },
    onSubmit: async ({ changes, model }) => {
      let asset: File | null | undefined = model.asset;
      if (asset?.size === 0) {
        // If the asset is empty, this is only the placeholder for the existing file
        // and we don't want to update it.
        asset = undefined;
      }

      await update({
        variables: {
          ...changes,
          id: newsItem.id,
          asset,
          ...('meta' in changes ? { meta: parseJsonString(changes.meta) } : {}),
        },
      });
      close();
      submit();
    },
    onSubmitError: (error) => handleError(error),
  });

  return (
    <Modal
      open={open}
      title={newsItem.title || t.model.news.untitled}
      footer={
        <>
          <Button
            title={t.actions.cancel('')}
            onClick={() => {
              close();
            }}
          />
          <Button
            type="primary"
            title={t.actions.save('')}
            onClick={onSubmit}
            disabled={!dirty && !valid}
            loading={submissionStatus === 'submitting'}
          />
        </>
      }
    >
      <NewsForm fields={fields} />
    </Modal>
  );
}
